/**
 * <TutorLivesHistoryPage> — « Mon historique de lives » (page).
 *
 * Le legacy : `tutor/lives/live-history/TutorLivesHistoryScreen.js` (route `tutor-lives-history`,
 * ouverte par « Mes paiements » — `StripeExpressDashboardButton`) et sa table `LivesHistory`. Les
 * séances passées du masteur, la plus récente d'abord, une ligne par séance
 * (`<TutorHistoryOccurrenceRow>`) dans UNE carte-liste qu'on fait défiler page par page ; la
 * recherche en tête (l'ancien `ServerSearch`, « Rechercher... ») ; et le « i » de l'en-tête, qui
 * explique les paiements.
 *
 * Une table à colonnes ne tient pas sur un téléphone (l'ancienne défilait de côté) : chaque séance
 * devient une ligne de carte-liste, ses colonnes empilées. Le filtre par statut de l'ancien écran
 * était déjà commenté : il n'est pas porté.
 *
 * Contrôlée : la recherche, la page en cours et les états de chargement arrivent en props ; seule la
 * boîte d'aide (un état de vue) vit ici.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Icon } from "../../atoms/icons/Icon"
import { TextInput } from "../../atoms/TextInput"
import { CardList } from "../../molecules/CardList"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { EmptyState } from "../../molecules/EmptyState"
import { Header } from "../../molecules/Header"
import { InfiniteList } from "../../molecules/InfiniteList"
import { TutorHistoryOccurrenceRow } from "../../organisms/TutorHistoryOccurrenceRow"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { type UITutorHistoryOccurrence, type UITutorHistoryReport } from "../../../ports/primary/ui/types/tutorLives"
import { TutorHistoryMoreSkeleton, TutorLivesHistorySkeleton } from "./TutorLivesHistorySkeleton"

const TEXTS = {
    title: "Mon historique de lives",
    search: "Rechercher...",
    empty: "Aucun live pour l'instant.",
    emptySearch: "Aucun live ne correspond à ta recherche.",
    paymentsTitle: "Comment ça marche pour les paiements ?",
    paymentsMessage:
        "Lorsqu'une séance est validée, le montant est transféré vers ton solde Stripe. Tu peux alors suivre les virements automatiques depuis ton solde Stripe vers ton compte bancaire, en cliquant sur « Suivre mes paiements » sur la page d'accueil.",
    paymentsClose: "J'ai compris",
}

export type TutorLivesHistoryPageProps = {
    occurrences: UITutorHistoryOccurrence[]
    query: string
    onChangeQuery: (query: string) => void
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    refreshing?: boolean
    onRefresh?: () => void
    loadingMore?: boolean
    hasMore?: boolean
    onLoadMore?: () => void
    loadMoreError?: string | null
    onRetryLoadMore?: () => void
    /** Le nombre de séances de la recherche : « 5 / 42 ». */
    totalCount?: number
    onClose?: () => void
    /** Le nom d'un élève : sa fiche. */
    onOpenStudent?: (report: UITutorHistoryReport) => void
    /** « Voir le rapport » : le rapport de la séance (`r`). */
    onOpenReport?: (report: UITutorHistoryReport) => void
    /** Storybook : la boîte des paiements ouverte, la première séance dépliée. */
    initialPaymentsHelpOpen?: boolean
    initialExpandedId?: UITutorHistoryOccurrence["id"]
    testID?: string
}

export function TutorLivesHistoryPage(props: TutorLivesHistoryPageProps) {
    const {
        occurrences,
        query,
        onChangeQuery,
        loading = false,
        error,
        onRetry,
        refreshing,
        onRefresh,
        loadingMore,
        hasMore,
        onLoadMore,
        loadMoreError,
        onRetryLoadMore,
        totalCount,
        onClose,
        onOpenStudent,
        onOpenReport,
        initialPaymentsHelpOpen = false,
        initialExpandedId,
        testID,
    } = props
    const [helpOpen, setHelpOpen] = React.useState(initialPaymentsHelpOpen)

    const header = (
        <Header
            title={TEXTS.title}
            left={<IconButton iconName="times" appearance="transparent" iconColor="primary" onPress={onClose} />}
            right={
                <IconButton
                    iconName="info-circle"
                    appearance="transparent"
                    iconColor="primary"
                    onPress={() => setHelpOpen(true)}
                />
            }
        />
    )

    const search = (
        <View style={styles.search}>
            <TextInput
                value={query}
                onChangeText={onChangeQuery}
                placeholder={TEXTS.search}
                left={<Icon name="search" size={16} color="text-muted" />}
            />
        </View>
    )

    return (
        <PageTemplate
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<TutorLivesHistorySkeleton />}
            error={error}
            onRetry={onRetry}
            scroll={false}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            <InfiniteList
                data={occurrences}
                keyExtractor={(occurrence) => String(occurrence.id)}
                header={search}
                gap="4"
                renderItem={(occurrence) => (
                    <CardList>
                        <TutorHistoryOccurrenceRow
                            occurrence={occurrence}
                            defaultExpanded={occurrence.id === initialExpandedId}
                            onOpenStudent={onOpenStudent}
                            onOpenReport={onOpenReport}
                        />
                    </CardList>
                )}
                emptyMessage={query !== "" ? TEXTS.emptySearch : undefined}
                refreshing={refreshing}
                onRefresh={onRefresh}
                loadingMore={loadingMore}
                moreSkeleton={<TutorHistoryMoreSkeleton />}
                loadMoreError={loadMoreError}
                onRetryLoadMore={onRetryLoadMore}
                hasMore={hasMore}
                onEndReached={onLoadMore}
                totalCount={totalCount}
            />
            {occurrences.length === 0 && query === "" && <EmptyState title={TEXTS.empty} iconName="calendar" />}
            <ConfirmDialog
                visible={helpOpen}
                title={TEXTS.paymentsTitle}
                message={TEXTS.paymentsMessage}
                confirmLabel={TEXTS.paymentsClose}
                hideCancel
                onConfirm={() => setHelpOpen(false)}
                onCancel={() => setHelpOpen(false)}
            />
        </PageTemplate>
    )
}

const styles = StyleSheet.create((theme) => ({
    search: {
        marginBottom: theme.spacing["4"],
    },
}))
